import React, { PureComponent } from 'react';
import PropTypes from 'prop-types';
import classNames from 'classnames';
import { debounce } from 'lodash';

import Icon from 'widgets/Icon/Icon';
import { noop } from 'utils/func';

const TOGGLE_HEIGHT = 80;

export default class ExpandableSection extends PureComponent {
  static propTypes = {
    className: PropTypes.string,
    collapsedHeight: PropTypes.string,
    expandedHeight: PropTypes.string,
    children: PropTypes.node,
    onToggle: PropTypes.func,
    preventStateReset: PropTypes.bool,
  };

  static defaultProps = {
    collapsedHeight: '320px',
    onToggle: noop,
  };

  state = {
    shouldBeExpandable: false,
    expanded: false,
  };

  componentDidMount() {
    this.updateHeights();
    window.addEventListener('resize', this.debouncedResize);
    window.addEventListener('scroll', this.suppressResizeOnScroll);
  }

  componentDidUpdate(prevProps) {
    if (this.props.children !== prevProps.children) {
      if (!this.props.preventStateReset) {
        this.resetExpandedState();
      }
      this.updateHeights();
    }
  }

  componentWillUnmount() {
    this.debouncedResize.flush();
    window.removeEventListener('resize', this.debouncedResize);
    window.removeEventListener('scroll', this.suppressResizeOnScroll);
  }

  resetExpandedState() {
    if (this.state.expanded) {
      this.setState({
        expanded: false,
      });
    }
  }

  wrapper = React.createRef();

  suppressResizeOnScroll = () => {
    this.detachDebouncedResize();
    this.reattachDebouncedResize();
  };

  debouncedResize = debounce(() => { this.updateHeights(); }, 200);

  detachDebouncedResize = debounce(() => {
    window.removeEventListener('resize', this.debouncedResize);
  }, 100, { leading: true, trailing: false });

  reattachDebouncedResize = debounce(() => {
    window.addEventListener('resize', this.debouncedResize);
  }, 100)

  toggle = () => {
    const initialStateOfSection = this.state.expanded;
    this.setState(prevState => ({
      expanded: !prevState.expanded,
    }));
    this.props.onToggle(initialStateOfSection);
  };

  updateHeights = () => {
    if (this.wrapper.current) {
      const { scrollHeight, clientHeight } = this.wrapper.current;
      const shouldBeExpandable = scrollHeight > clientHeight;
      const expandedHeight = this.props.expandedHeight || `${scrollHeight + TOGGLE_HEIGHT}px`;
      if (this.state.shouldBeExpandable !== shouldBeExpandable || this.state.expandedHeight !== expandedHeight) {
        this.setState({
          shouldBeExpandable,
          expandedHeight,
        });
      }
    }
  };

  renderExpandButton = () => {
    const { expanded } = this.state;

    return (
      <div className="gn-expandableSection__toggle" aria-hidden>
        <span className="gn-divider gn-expandableSection__toggleDivider" />
        <button // eslint-disable-line jsx-a11y/no-static-element-interactions, jsx-a11y/click-events-have-key-events
          onClick={this.toggle}
          className="gn-expandableSection__toggleButton"
        >
          {expanded ? 'Show less' : 'Show more'}
          <Icon name={expanded ? 'chevron-up' : 'chevron-down'} />
        </button>
      </div>
    );
  };

  render() {
    const { children, className, collapsedHeight } = this.props;
    const { shouldBeExpandable, expanded, expandedHeight } = this.state;

    const heightStyle = { maxHeight: expanded ? expandedHeight : collapsedHeight };

    return (
      <div
        className={classNames(
          className,
          'gn-expandableSection',
          {
            'gn-expandableSection--isExpandable': shouldBeExpandable,
            'gn-expandableSection--expanded': expanded,
          }
        )}
        ref={this.wrapper}
        style={heightStyle}
      >
        {children}
        {shouldBeExpandable && this.renderExpandButton()}
      </div>
    );
  }
}
